export type Locale = 'nl' | 'en';

export interface LocalizedString {
  nl: string;
  en: string;
}

export interface Experience {
  period: LocalizedString;
  role: LocalizedString;
  company: string;
  description: LocalizedString;
}

export interface Education {
  period: string;
  degree: LocalizedString;
  institution: string;
  description: LocalizedString;
}

export interface Certificate {
  year: string;
  title: string;
  issuer: string;
}

export interface SkillGroup {
  label: LocalizedString;
  skills: string;
}

export interface Language {
  name: LocalizedString;
  level: LocalizedString;
}

export const CV_PROFILE: LocalizedString = {
  nl: 'Creative developer die de kloof overbrugt tussen meeslepende 3D-frontend-ervaringen (Three.js / WebGL) en robuuste, schaalbare e-commerce-backends (Symfony / Shopify). Sterk in UI-kwaliteit, microdetail en vloeiende gebruikerservaringen, van concept tot pixel-perfecte realisatie.',
  en: 'Creative developer bridging the gap between immersive 3D frontend experiences (Three.js / WebGL) and robust, scalable e-commerce backends (Symfony / Shopify). Strong in UI quality, micro-detail and fluid user experiences, from concept to pixel-perfect delivery.',
};

export const CV_AI: LocalizedString = {
  nl: 'Ik zet AI in om mijn werk grondiger en gedetailleerder te maken, niet om het uit handen te geven. Ik geloof in AI dat door mensen wordt gestuurd: het oordeel, de smaak en de richting blijven menselijk, terwijl AI helpt om sneller tot het perfecte resultaat te komen en de kwaliteit te bewaken.',
  en: 'I use AI to make my work more thorough and detailed, not to hand it off. I believe in AI guided by people: the judgement, taste and direction stay human, while AI helps me reach the perfect result faster and maintain the quality.',
};

export const CV_PROFILE_FORMAL: LocalizedString = {
  nl: 'Software developer met expertise in front-end engineering (Javascript, TypeScript, React, Three.js / WebGL) en schaalbare backend- en e-commercesystemen (PHP, Symfony, Shopify, Shopware). Ruime ervaring met het ontwikkelen en onderhouden van bedrijfskritische, hoogbelaste platformen binnen gestructureerde teams. Werkt nauwgezet en discreet, met consequente aandacht voor codekwaliteit, beveiliging, documentatie en betrouwbaarheid, van analyse en architectuur tot implementatie en oplevering. Ik groei graag in de breedte, nieuwe technieken leren dagen me uit en daar ligt dan ook mijn sterkte.',
  en: 'Software developer with expertise in front-end engineering (Javascript, TypeScript, React, Three.js / WebGL) and scalable backend and e-commerce systems (PHP, Symfony, Shopify, Shopware). Extensive experience developing and maintaining business-critical, high-load platforms within structured teams. Works diligently and discreetly, with consistent attention to code quality, security, documentation and reliability, from analysis and architecture through to implementation and delivery. I like to grow broadly — learning new techniques challenges me, and that\'s where my strength lies.',
};

export const CV_AI_FORMAL: LocalizedString = {
  nl: 'Ik gebruik AI uitsluitend als ondersteunend hulpmiddel om kwaliteit en grondigheid te verhogen, nooit om besluitvorming of verantwoordelijkheid over te dragen. Oordeel, richting en eindverantwoordelijkheid blijven bij de ontwikkelaar, met blijvende aandacht voor de vertrouwelijkheid en integriteit van gegevens.',
  en: 'I use AI strictly as a supporting tool to raise quality and thoroughness, never to delegate decision-making or responsibility. Judgement, direction and final accountability remain with the developer, with ongoing regard for the confidentiality and integrity of data.',
};

export const CV_TITLE_FORMAL: LocalizedString = {
  nl: 'Software developer',
  en: 'Software developer',
};

export const CV_INTERESTS_FORMAL: LocalizedString = {
  nl: 'Sportklimmen, volleybal, muziek, 3D-graphics',
  en: 'Sport climbing, volleyball, music, 3D graphics',
};

export const CV_INTERESTS: LocalizedString = {
  nl: 'Muurklimmen, volleybal, muziek, Three.js',
  en: 'Rock climbing, volleyball, music, Three.js',
};

export const CV_CONTACT = {
  name: 'Jolan De Nef',
  title: {
    nl: 'Creative developer',
    en: 'Creative developer',
  } as LocalizedString,
  phone: '+32 471 32 54 79',
  email: 'contact@jolandenef.dev',
  location: {
    nl: 'Mechelen, België',
    en: 'Mechelen, Belgium',
  } as LocalizedString,
  website: 'jolandenef.dev',
  linkedin: 'linkedin.com/in/jolandenef',
};

export const CV_SKILL_GROUPS: SkillGroup[] = [
  {
    label: { nl: 'Frontend Expertise', en: 'Frontend Expertise' },
    skills: 'Javascript, TypeScript, React, Three.js, WebGL, GSAP',
  },
  {
    label: { nl: 'Backend & e-commerce', en: 'Backend & e-commerce' },
    skills: 'PHP, Symfony, Shopware, Shopify',
  },
  {
    label: { nl: 'Tools & infrastructuur', en: 'Tools & infrastructure' },
    skills: 'Git, CLI, Linux sysadmin, Vite',
  },
];

export const CV_LANGUAGES: Language[] = [
  { name: { nl: 'Nederlands', en: 'Dutch' }, level: { nl: 'Moedertaal', en: 'Native' } },
  { name: { nl: 'Engels', en: 'English' }, level: { nl: 'Professioneel', en: 'Professional' } },
  { name: { nl: 'Frans', en: 'French' }, level: { nl: 'Basis', en: 'Basic' } },
];

export const CV_EXPERIENCE: Experience[] = [
  {
    period: { nl: '2022 – heden', en: '2022 – present' },
    role: { nl: 'Zelfstandige in bijberoep', en: 'Freelance developer (side practice)' },
    company: '',
    description: {
      nl: 'Ontwikkelde een cross-platform mobiele app (Cordova), custom content-websites en een eigen portfolio als creatief laboratorium, volledig in React en ThreeJS. Verantwoordelijk voor de volledige cyclus: concept, architectuur, implementatie en deployment.',
      en: 'Developed a cross-platform mobile app (Cordova), custom content websites, and my own portfolio as a creative lab, all built in React and ThreeJS. Owned the full cycle: concept, architecture, implementation, and deployment.',
    },
  },
  {
    period: { nl: 'feb 2021 – heden', en: 'Feb 2021 – present' },
    role: { nl: 'E-commerce developer', en: 'E-commerce developer' },
    company: 'Meteor, Kontich',
    description: {
      nl: 'Werkte binnen grote teams aan complexe backend-structuren voor e-commerce op Shopify en Shopware. Droeg in een Symfony-backend-omgeving bij aan schaalbaarheid en codekwaliteit van meerdere drukbezochte e-commerce-producten.',
      en: 'Worked in large teams on complex backend structures for e-commerce on Shopify and Shopware. Contributed within a Symfony backend environment to the scalability and code quality of multiple high-traffic e-commerce products.',
    },
  },
  {
    period: { nl: 'jan 2019 – jan 2021', en: 'Jan 2019 – Jan 2021' },
    role: { nl: 'Webdeveloper', en: 'Web developer' },
    company: 'Codeurs bvba, Hofstade',
    description: {
      nl: 'Aangenomen na een succesvolle stage in 2018. Co-ontwikkelde pixel-perfecte React-webapplicaties van nul, met strikte aandacht voor UI-kwaliteit en UX-richtlijnen. Droeg bij aan diverse klantenprojecten en verbeterde de componentenbibliotheek van het team.',
      en: 'Hired after a successful 2018 internship. Co-developed pixel-perfect React web applications from scratch, ensuring strict adherence to UI quality and UX guidelines. Contributed to diverse client projects and improved the team\'s component library.',
    },
  },
];

/**
 * Formal rewrite of the experience entries, aligned by index with `CV_EXPERIENCE`. Same periods and
 * employers; roles and descriptions restated in professional, outcome-focused language.
 */
export const CV_EXPERIENCE_FORMAL: Experience[] = [
  {
    period: { nl: '2022 – heden', en: '2022 – present' },
    role: { nl: 'Zelfstandig developer (bijberoep)', en: 'Independent developer (part-time)' },
    company: '',
    description: {
      nl: 'Ontwikkeling van een cross-platform mobiele applicatie (Cordova) en diverse maatwerk-webprojecten in React. Volledige verantwoordelijkheid over de levenscyclus: analyse, architectuur, implementatie, deployment en onderhoud, met zelfstandig beheer van planning, kwaliteit en vertrouwelijkheid van klantgegevens.',
      en: 'Development of a cross-platform mobile application (Cordova) and various bespoke web projects in React. Full ownership of the lifecycle: analysis, architecture, implementation, deployment and maintenance, independently managing planning, quality and the confidentiality of client data.',
    },
  },
  {
    period: { nl: 'feb 2021 – heden', en: 'Feb 2021 – present' },
    role: { nl: 'E-commerce developer', en: 'E-commerce developer' },
    company: 'Meteor, Kontich',
    description: {
      nl: 'Bijdrage aan bedrijfskritische backend-architecturen voor e-commerce op Shopify en Shopware binnen grote, internationaal samengestelde ontwikkelteams. Verantwoordelijk voor schaalbaarheid, beveiliging en codekwaliteit van meerdere hoogbelaste platformen in een Symfony-omgeving, met strikte naleving van review- en teststandaarden.',
      en: 'Contributed to business-critical backend architectures for e-commerce on Shopify and Shopware within large, internationally composed development teams. Responsible for the scalability, security and code quality of multiple high-load platforms in a Symfony environment, with strict adherence to review and testing standards.',
    },
  },
  {
    period: { nl: 'jan 2019 – jan 2021', en: 'Jan 2019 – Jan 2021' },
    role: { nl: 'Webdeveloper', en: 'Web developer' },
    company: 'Codeurs bvba, Hofstade',
    description: {
      nl: 'Aangeworven na een stage in 2018. Ontwikkeling van React-webapplicaties met strikte naleving van kwaliteits-, UI- en UX-standaarden. Betrouwbare bijdrage aan uiteenlopende klantprojecten onder vertrouwelijkheid, en aan de uitbreiding en documentatie van de gedeelde componentenbibliotheek van het team.',
      en: 'Recruited following an internship in 2018. Development of React web applications with strict adherence to quality, UI and UX standards. Reliable contribution to a range of client projects under confidentiality, and to the expansion and documentation of the team\'s shared component library.',
    },
  },
];

export const CV_EDUCATION: Education[] = [
  {
    period: 'sep 2015 – jul 2018',
    degree: {
      nl: 'Interactive Multimedia Design – optie Development',
      en: 'Interactive Multimedia Design – Development track',
    },
    institution: 'Thomas More Mechelen',
    description: {
      nl: 'Brede basis in design én development. Koos in het tweede jaar voor de development-optie: backend (Laravel, ASP.net) en frontend (React).',
      en: 'Broad foundation in both design and development. Chose the development track in year two: backend (Laravel, ASP.net) and frontend (React).',
    },
  },
  {
    period: 'sep 2008 – jul 2014',
    degree: {
      nl: 'ASO Wetenschappen-Wiskunde',
      en: 'General secondary – Sciences & Mathematics',
    },
    institution: 'Scheppersinstituut Mechelen',
    description: {
      nl: '',
      en: '',
    },
  },
];

export const CV_CERTIFICATES: Certificate[] = [
  {
    year: '2023',
    title: 'Three.js Journey',
    issuer: 'Bruno Simon',
  },
];

export const CV_LABELS: Record<string, LocalizedString> = {
  curriculumVitae: { nl: 'Curriculum Vitae', en: 'Curriculum Vitae' },
  profile: { nl: 'Profiel', en: 'Profile' },
  ai: { nl: 'AI', en: 'AI' },
  experience: { nl: 'Werkervaring', en: 'Experience' },
  education: { nl: 'Opleidingen', en: 'Education' },
  certificates: { nl: 'Certificaten', en: 'Certificates' },
  personalDetails: { nl: 'Persoonlijke gegevens', en: 'Personal details' },
  interests: { nl: 'Interesses', en: 'Interests' },
  skills: { nl: 'Vaardigheden', en: 'Skills' },
  languages: { nl: 'Talen', en: 'Languages' },
  language: { nl: 'Taal', en: 'Language' },
  downloadPdf: { nl: 'Afdrukken / PDF', en: 'Print / PDF' },
  design: { nl: 'Vormgeving', en: 'Design' },
  designStudio: { nl: 'Studio', en: 'Studio' },
  designFormal: { nl: 'Formeel', en: 'Formal' },
  reference: { nl: 'Referentie', en: 'Reference' },
  issued: { nl: 'Opgemaakt', en: 'Issued' },
  summary: { nl: 'Samenvatting', en: 'Summary' },
  location: { nl: 'Locatie', en: 'Location' },
};

export type CVDesign = 'studio' | 'formal';

/**
 * @param url - Request or page URL whose `?lang` picks the locale
 * @returns `nl` for `?lang=nl`, otherwise the default `en`
 */
export function cvLocale(url: URL | URLSearchParams): Locale {
  const params = url instanceof URL ? url.searchParams : url;
  return 'nl' === params.get('lang') ? 'nl' : 'en';
}

/**
 * Stamped by the route loaders so the printed date renders on the server and hydrates without a
 * mismatch.
 *
 * @param locale - Locale to format the date for
 * @returns Today's date formatted for print, plus its year for document references
 */
export function issuedStamp(locale: Locale) {
  const now = new Date();
  return {
    issued: now.toLocaleDateString('nl' === locale ? 'nl-BE' : 'en-GB', { year: 'numeric', month: 'short', day: '2-digit' }),
    year: now.getFullYear(),
  };
}

/**
 * Resolves the copy for a given design variant: the Formal variant gets the measured overrides, the
 * Studio variant keeps the warm originals.
 *
 * @param design - Active design variant
 * @returns The profile, AI note, title, interests and experience entries for that variant
 */
export function cvContent(design: CVDesign) {
  const formal = 'formal' === design;
  return {
    profile: formal ? CV_PROFILE_FORMAL : CV_PROFILE,
    ai: formal ? CV_AI_FORMAL : CV_AI,
    title: formal ? CV_TITLE_FORMAL : CV_CONTACT.title,
    interests: formal ? CV_INTERESTS_FORMAL : CV_INTERESTS,
    experience: formal ? CV_EXPERIENCE_FORMAL : CV_EXPERIENCE,
  };
}
